import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Filter, TrendingUp } from 'lucide-react';
import { ArtCategory } from '../types';
import { postsService } from '../services/postsService';
import FollowButton from './FollowButton';
import { followsService } from '../services/followsService';
import MasonryGrid from './MasonryGrid';
import { useLanguage } from '../context/LanguageContext';

const ExploreView: React.FC = () => {
  const { select } = useLanguage();
  const location = useLocation();
  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Sculpture' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Dance' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Makeup' }),
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ArtCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'trending'>('trending');
  const [posts, setPosts] = useState<Record<string, unknown>[]>([]);
  const [recommendedUsers, setRecommendedUsers] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleTagSearch = (event: Event) => {
      const customEvent = event as CustomEvent<{ searchQuery?: string; category?: ArtCategory }>;
      const { searchQuery: query, category } = customEvent.detail;
      if (query) {
        setSearchQuery(query);
      }
      if (category) {
        setSelectedCategory(category);
      }
    };

    window.addEventListener('tagSearch', handleTagSearch);
    window.addEventListener('categorySearch', handleTagSearch);
    
    return () => {
      window.removeEventListener('tagSearch', handleTagSearch);
      window.removeEventListener('categorySearch', handleTagSearch);
    };
  }, []);

  useEffect(() => {
    const state = location.state as { category?: ArtCategory } | null;
    if (state && state.category) {
      setSelectedCategory(state.category);
    }
  }, [location]);

  // Effect for debouncing the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce time

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]); // Only re-run if searchQuery changes

  // Effect to load posts and recommended users, triggered by filters/sort/debounced search
  useEffect(() => {
    loadPosts();
    loadRecommendedUsers(); // This can run whenever dependencies change, or you might prefer it once on mount

    // Listen for new posts created
    const handlePostCreated = () => {
      loadPosts(); // Refresh posts when a new one is created
    };

    window.addEventListener('postCreated', handlePostCreated);

    return () => {
      window.removeEventListener('postCreated', handlePostCreated);
    };
  }, [selectedCategory, sortBy, debouncedSearchQuery]); // Added debouncedSearchQuery to dependencies

  const loadPosts = async () => {
    try {
      setLoading(true);
      console.log('🔍 ExploreView: Loading posts with ads...');
      const data = await postsService.getPosts({
        category: selectedCategory,
        sortBy,
        searchQuery: debouncedSearchQuery, // Pass debounced query to service
        limit: 20,
        includeAds: true
      });
      console.log('🔍 ExploreView: Loaded', data.length, 'posts (including ads)');
      setPosts(data);
    } catch (err) {
      setError(select({ es: 'Error al cargar las publicaciones', en: 'Failed to load posts' }));
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendedUsers = async () => {
    try {
      const users = await followsService.getRecommendedUsers(5);
      setRecommendedUsers(users);
    } catch (err) {
      console.error('Error loading recommended users:', err);
    }
  };

  // The client-side filter is no longer needed if search is server-side.
  // If you still need client-side filtering for other reasons (e.g., small dataset or specific local filtering),
  // keep this or a modified version. For now, assuming server-side search.
  const filteredPosts = posts; // Assuming posts are already filtered/searched by the backend now

  const handleLike = async (postId: string) => {
    try {
      // Optimistic UI update could be added here for a better user experience
      const isLiked = await postsService.checkIfLiked(postId);
      if (isLiked) {
        await postsService.unlikePost(postId);
      } else {
        await postsService.likePost(postId);
      }
      // Optionally reload posts if like counts need to be reflected from server immediately
      // loadPosts();
    } catch (err) {
      if (!(err instanceof Error && err.message?.includes('already liked'))) {
        console.error('Error toggling like:', err);
      }
      // Consider showing a user-friendly error message here
    }
  };

  const handleSave = (postId: string) => {
    console.log('Saved post:', postId);
    // Implement actual save logic here
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {select({ es: 'Explorar arte', en: 'Explore art' })}
        </h1>

        {/* Recommended Artists */}
        {recommendedUsers.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">
              {select({ es: 'Artistas recomendados', en: 'Recommended artists' })}
            </h2>
            <div className="flex space-x-4 overflow-x-auto pb-2">
              {recommendedUsers.map((artist) => (
                <div key={artist.id} className="flex-shrink-0 text-center">
                  <div className="mb-2">
                    <img
                      src={artist.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=64&h=64&fit=crop'}
                      alt={artist.full_name || 'Artista'}
                      className="w-16 h-16 rounded-full object-cover mx-auto mb-2"
                    />
                    <h3 className="font-medium text-gray-900 text-sm">{artist.full_name}</h3>
                    <p className="text-xs text-gray-500">@{artist.username}</p>
                    {artist.art_categories && artist.art_categories[0] && (
                    <p className="text-xs text-purple-600 mt-1">
                        {categoryLabels[artist.art_categories[0] as ArtCategory]}
                      </p>
                    )}
                  </div>
                  <FollowButton
                    userId={artist.id}
                    username={artist.username}
                    size="sm"
                    variant="primary"
                    showIcon={false}
                    onFollowChange={() => loadRecommendedUsers()}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={select({ es: 'Buscar arte, artistas, técnicas...', en: 'Search art, artists, techniques...' })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} // Updates searchQuery, which then debounces
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as ArtCategory | 'all')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">{select({ es: 'Todas las categorías', en: 'All categories' })}</option>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'recent' | 'popular' | 'trending')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="trending">{select({ es: 'Tendencias', en: 'Trending' })}</option>
              <option value="popular">{select({ es: 'Más populares', en: 'Most popular' })}</option>
              <option value="recent">{select({ es: 'Más recientes', en: 'Most recent' })}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-48 rounded-xl"></div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="w-full">
            <MasonryGrid
              posts={filteredPosts.map(post => {
                console.log('Post structure:', post);
                return {
                  ...post,
                  type: post.type || 'post',
                  author: post.profiles || post.author || {
                    id: post.author_id || '',
                    username: 'usuario_desconocido',
                    full_name: 'Usuario Desconocido',
                    avatar_url: null,
                    is_premium: false
                  },
                  media: post.media_files || post.media || [],
                  comments: post.comments || [],
                  likes: post.likes_count || post.likes || 0,
                  views: post.views_count || post.views || 0,
                  createdAt: post.created_at || post.createdAt
                };
              })}
              onLike={handleLike}
              onSave={handleSave}
            />
          </div>
        ) : ( // Added this closing else block for clarity, though it's implicitly handled
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {select({ es: 'No se encontraron resultados', en: 'No results found' })}
            </h3>
            <p className="text-gray-500">
              {select({ es: 'Intenta con otros términos de búsqueda o categorías', en: 'Try different search terms or categories' })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreView;
